import React, { memo } from "react";

import type { AgentProgress } from "@/features/knowledge-graph/types/progressMessageBubbleTypes";

import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";

/**
 * Resolves the per-step container className for a progress row based on status.
 * @param status - The agent step status (running / complete / error / other)
 * @returns The semantic-token className (surface + border + opacity) for the row
 * @remarks SE-5358: running → surface-subtle bg + accent-primary border (2px);
 *   error → status-error border; otherwise surface-base bg + outline-default
 *   border, with status-driven opacity.
 */
const stepBoxClassName = (status: string): string => {
  if (status === "running") {
    return "border-2 border-accent-primary bg-surface-subtle";
  }
  if (status === "error") {
    return "border-status-error bg-surface-base opacity-90";
  }
  if (status === "complete") {
    return "border-outline-default bg-surface-base opacity-80";
  }
  return "border-outline-default bg-surface-base opacity-60";
};

/**
 * Props for QueryProgressBarView component
 * @property agentProgress - Array of agent progress steps during query processing
 * @property statusText - Current status text to display
 * @property isComplete - Whether query processing is complete
 * @property hasError - Whether an error occurred during processing
 * @property totalAgents - Total number of agents involved in processing
 * @property completedAgents - Number of agents that have completed processing
 * @property getAgentIcon - Function to get icon for agent type
 * @property getStatusIcon - Function to get icon for status type
 * @property formatAgentName - Function to format agent name for display
 * @property formatDuration - Function to format duration for display
 */
interface QueryProgressBarViewProps {
  agentProgress: AgentProgress[];
  statusText: string;
  isComplete: boolean;
  hasError: boolean;
  totalAgents: number;
  completedAgents: number;
  getAgentIcon: (agent: string) => string;
  getStatusIcon: (status: string) => string;
  formatAgentName: (agent: string) => string;
  formatDuration: (duration: number) => string;
}

/**
 * View component for displaying query progress bar with agent status
 * Shows progress indicators, agent icons, and completion status
 * @remarks SE-5358: colors are semantic design-system tokens (surface-base /
 *   surface-subtle bg, outline-default / accent-primary / status-error border,
 *   content-primary text) via NativeWind `dark:` variants — replacing the
 *   bespoke ChatThemeColors `colors` prop.
 * @remarks SE-5362: routed through the closed atom layer. SE-5358 had already
 *   moved every style to semantic-token classNames, so the remaining raw
 *   react-native `Text` carried no runtime `style` prop and swaps cleanly to
 *   the `Text` atom (className-only); the file drops off the import-closure
 *   permanentWaivers list.
 * @param root0 - Component props
 * @param root0.agentProgress - Array of agent progress steps during query processing
 * @param root0.statusText - Current status text to display
 * @param root0.isComplete - Whether query processing is complete
 * @param root0.hasError - Whether an error occurred during processing
 * @param root0.totalAgents - Total number of agents involved in processing
 * @param root0.completedAgents - Number of agents that have completed processing
 * @param root0.getAgentIcon - Function to get icon for agent type
 * @param root0.getStatusIcon - Function to get icon for status type
 * @param root0.formatAgentName - Function to format agent name for display
 * @param root0.formatDuration - Function to format duration for display
 */
const QueryProgressBarView = ({
  agentProgress,
  statusText,
  isComplete: _isComplete,
  hasError: _hasError,
  totalAgents: _totalAgents,
  completedAgents: _completedAgents,
  getAgentIcon,
  getStatusIcon,
  formatAgentName,
  formatDuration,
}: QueryProgressBarViewProps) =>
  agentProgress.length === 0 ? (
    <Box className="px-4 py-1">
      <Box className="rounded-md border border-outline-default bg-surface-base p-3">
        <Box className="flex-row items-center">
          <Text variant="body" className="text-content-primary">
            {statusText}
          </Text>
        </Box>
      </Box>
    </Box>
  ) : (
    <Box className="px-4 py-1">
      {/* Individual step boxes - Always vertical layout regardless of screen size */}
      <Box className="space-y-2">
        {agentProgress.map((progress, index) => (
          <Box
            key={`${progress.agent}-${index}`}
            className={`flex-row items-start gap-3 rounded-md border px-4 py-3 ${stepBoxClassName(
              progress.status
            )}`}
          >
            {/* Agent icon */}
            <Text variant="unstyled" className="text-title-sm">
              {getAgentIcon(progress.agent)}
            </Text>

            {/* Agent name and status */}
            <Box className="flex-1">
              <Text variant="body-strong" className="text-content-primary">
                {formatAgentName(progress.agent)}
              </Text>
              {!!progress?.message && (
                <Text
                  variant="caption"
                  className="pt-micro text-content-primary opacity-70"
                >
                  {progress.message}
                </Text>
              )}
              {progress?.duration != null &&
                progress?.status === "complete" && (
                  <Text
                    variant="caption"
                    className="pt-micro text-content-primary opacity-50"
                  >
                    {formatDuration(progress.duration)}
                  </Text>
                )}
            </Box>

            {/* Status icon */}
            <Text variant="unstyled" className="text-title-sm">
              {getStatusIcon(progress.status)}
            </Text>
          </Box>
        ))}
      </Box>
    </Box>
  );

QueryProgressBarView.displayName = "QueryProgressBarView";
export default memo(QueryProgressBarView);
